import React from "react"
import Quote from "@components/quote"
import CenteredTextBlock from "@components/blocks/centered-text"
import TextBlock from "@components/blocks/text"
import WufooFormBlock from "@components/blocks/wufoo-form"
import ContactForm from "@components/blocks/contact-form"
import SupportPoints from "@components/support-points"
import Values from "@components/blocks/values"
import Perks from "@components/blocks/perks"
import ImageBlock from "@components/image-block"
import ImagesBlock from "@components/image-block/imagesBlock"
import Testimonials from "@components/blocks/testimonials"
import EmbeddedHTML from "@components/blocks/embedded-html"
import CrossSells from "../cross-sells"
import LogoList from "../logo-list"
import GreenhouseList from "@components/greenhouse/greenhouse-list"
import HeroSlider from "@components/hero-slider"
import ProductSlider from "@components/product-slider"
import BlogSlider from "@components/blog-slider"
import PressSlider from "@components/press-slider"
import ComparisonTable from "@components/comparison-table"
import ContentGrid from "@components/blocks/content-grid"
import VideoBlock from "@components/video"
import Section from "@components/section"
import Newsletter from "@components/newsletter"
import CollectionList from "@components/collection-list"
import InfoColumns from "@components/info-columns"
import BundleShowcase from "@components/bundle-showcase"
import InstagramFeed from "@components/instagram-feed"
import SectionBuilder from "@components/section-builder"
import RichText from "@components/rich-text"
import ButtonGroup from "@components/button-group"
import Image from "@components/image"
import Vimeo from "@u-wave/react-vimeo"
import HelpCenter from "../help-center"
import StampedEmbed from "../reviews/stamped-embed"
import Hero from "@components/hero"
import NewHero from "@components/new-hero"
import { TokiReferAFriend } from "../widgets/toki"
import Pdf from "@components/pdf"
import AccordionSection from "../accordions"
import EmbeddedProductView from "@components/embedded-product-view"

function getBlock(block, showsInPDP, className) {
  const blockMap = {
    bestSellers: CrossSells, 
    logoList: LogoList, 
    contactForm: ContactForm, 
    supportPoints: SupportPoints, 
    mainHero: HeroBlock, 
    hero: Hero, 
    newHero: NewHero,
    heroSlider: HeroSlider, 
    imageBlock: ImageBlock, 
    contentImages: ImagesBlock,  
    centeredText: CenteredTextBlock,  
    centeredCopy: CenteredTextBlock,  
    greenhouseList: GreenhouseList, 
    perkList: Perks, 
    testimonials: Testimonials, 
    copy: TextBlock,  
    richTextObject: RichText, 
    text: TextBlock,  
    quote: QuoteBlock,  
    valueList: Values, 
    wufooForm: WufooFormBlock,  
    embeddedHTML: EmbeddedHTML,  
    carousel: Testimonials, 
    productSlider: ProductSlider, 
    blogSlider: BlogSlider, 
    pressSlider: PressSlider,
    comparisonTable: ComparisonTable, 
    cta: QuoteBlock, 
    video: VideoBlock,
    contentGrid: ContentGrid, 
    newsletterBanner: Newsletter, 
    collectionList: CollectionList, 
    infoColumns: InfoColumns, 
    bundleShowcase: BundleShowcase, 
    instaFeed: InstagramFeed, 
    sectionBuilder: SectionBuilder, 
    helpCenter: HelpCenter, 
    reviewsWidget: StampedEmbed, 
    referAFriend: TokiReferAFriend, 
    pdf: Pdf,
    accordionSection: AccordionSection,
    embeddedProductView: EmbeddedProductView,
  }
  const { _type, _key } = block

  if (typeof blockMap[_type] === "undefined") {
    return <h1>Not supported: {_type}</h1>
  }

  const Component = blockMap[_type]

  function isSlider() {
    const sliderComponents = [
      "carousel",
      "heroSlider",
      "productSlider",
      "blogSlider",
      "pressSlider",
      "bundleShowcase",
      "instaFeed",
    ]
    return sliderComponents.includes(_type);
  }

  const settings = () => {
    const oldModules = [
      'mainHero',
      'copy',
      'valueList',
    ];

    if (oldModules.includes(_type)) {
      return {
        id: block.id,
        isContainer: true,
        classname: `${block?.defaultSettings?.classname || ''} overflow-hidden ${className || ''}`
      }
    }

    return {
      ...block.defaultSettings,
      classname: `${block?.defaultSettings?.classname || ''} overflow-hidden ${className || ''}${Component === EmbeddedHTML && block?.hidden
        ? " hidden"
        : ""}`,
      removeContainer: _type === "heroSlider"
    }
  }

  const noTitleSections = [
    "mainHero",
    "contactForm",
    "comparisonTable",
  ]

  const sectionProps = {
    settings: settings(),
    key: _key,
    isSlider,
    innerClassname:
      Component === SectionBuilder
        ? "w-[calc(100%+0.625rem)] -ml-[5px] md:w-full md:m-0 grid grid-cols-12"
        : ""
      ||
      Component === CenteredTextBlock
        ? "grid grid-cols-12"
        : "",
    title: noTitleSections.includes(_type) ? null : block.title,
    containerClassname:
      Component === SectionBuilder && block.title
        ? 'flex flex-col gap-y-5'
        : '',
  }

  const componentProps = { 
    ...block, 
    isSlider: isSlider(), 
    showsInPDP: showsInPDP, 
    width: (Component === ImageBlock ? 1800 : Component === Pdf ? block.width : null),
    maxWidth: Component === VideoBlock ? 1800 : null
  }

  if (Component === CrossSells) return (
    <Section {...sectionProps}>
        <Component
          {...componentProps}
        />
    </Section>
  )

  return (
    <Section {...sectionProps}>
      <Component
        {...componentProps}
      />
    </Section>
  )
}

const ContentBuilder = ({
  blocks,
  sizeFilter,
  flavorFilter,
  configurationFilter,
  showsInPDP = false,
  className,
}) => {
  const showFilterIndexes = {}

  blocks?.forEach((block, index) => {
    const ignoredBlocks = [
      "servicesPoliciesInfo"
    ];
    
    if (!block || ignoredBlocks.includes(block?._type)) return

    const {
      sizeFilter: sizeFilterValue,
      flavorFilter: flavorFilterValue,
      configurationFilter: configurationFilterValue,
    } = block
    const hasNoVariantFilterValue =
      !sizeFilter && !flavorFilter && !configurationFilter
    const hasNoBlockFilterValue =
      !sizeFilterValue && !flavorFilterValue && !configurationFilterValue

    if (hasNoVariantFilterValue || hasNoBlockFilterValue) {
      showFilterIndexes[index] = true
      return
    }

    if (sizeFilter) {
      if (sizeFilterValue === "showForAllVariants") {
        showFilterIndexes[index] = true
      } else {
        showFilterIndexes[index] = sizeFilter === sizeFilterValue
      }
    } else if (flavorFilter) {
      if (flavorFilterValue === "showForAllVariants") {
        showFilterIndexes[index] = true
      } else {
        showFilterIndexes[index] = flavorFilter === flavorFilterValue
      }
    } else if (configurationFilter) {
      if (configurationFilterValue === "showForAllVariants") {
        showFilterIndexes[index] = true
      } else {
        showFilterIndexes[index] =
          configurationFilter === configurationFilterValue
      }
    } else {
      showFilterIndexes[index] = true
    }
  })

  const generatedBlocks = blocks
    ?.map(block => getBlock(block, showsInPDP, className))
    ?.filter((block, index) => showFilterIndexes[index])

  return <>{generatedBlocks}</>
}

export default ContentBuilder

const HeroBlock = ({
  title,
  MainHeroText,
  MainHeroImage,
  reverse,
  cta,
  secondaryCta,
  vimeoVideoId,
} ) => {
  return (
    <div className="grid grid-cols-12 max-md:gap-y-10 md:gap-10 w-full">
      <div className={`col-span-12 md:col-span-5 overflow-hidden rounded-xl ${reverse ? 'order-2' : ''}`}>
        {vimeoVideoId 
          ? <Vimeo
              className="bg-light-gray h-full w-full rounded-xl"
              video={"879214306"}
              autoplay={false}
              muted={false}
              controls={true}
              responsive
              loop={false}
            />
          : <Image image={MainHeroImage} className="rounded-xl"/>
        }
      </div>
      <div className="col-span-12 md:col-span-7 p-10 bg-light-gray rounded-xl flex flex-col gap-y-5 justify-between">
        <div className="flex flex-col gap-y-5">
          <h2 className="subheadline font-medium">{title}</h2>
          <RichText text={MainHeroText} paragraphClassName={'!mb-0'} />
        </div>
        <ButtonGroup list={[cta, secondaryCta]} isFullWidth={true} className={'!items-start'} />
      </div>
    </div>
  )
}

const QuoteBlock = ({
  cta,
  subtitle,
  credit,
  text,
  quoteText,
  bgColor,
  includeQuotationMarks,
}) => {
  return <Quote {...{ subtitle, text, quoteText, credit, includeQuotationMarks, cta }} />
}